import React from 'react';
import { warehouseImage } from '../mock';
import { capabilities } from '../mock';
import { Globe2, Package, Truck, Boxes, LineChart } from 'lucide-react';

const icons = [Globe2, Package, Truck, Boxes, LineChart];

export default function BusinessSection() {
  return (
    <section id="business" className="section" style={{ background: 'var(--sm-cream)', position: 'relative', overflow: 'hidden' }}>
      <div className="container-x">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'end', marginBottom: 72 }} className="biz-head">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
              <span className="hairline" />
              <span className="overline">The Operating Platform</span>
            </div>
            <h2 className="serif" style={{ fontSize: 'clamp(34px, 4.2vw, 56px)', lineHeight: 1.08, letterSpacing: '-0.015em', margin: 0, color: 'var(--sm-navy)' }}>
              A commercial operation integrating five coordinated functions.
            </h2>
          </div>
          <p style={{ fontSize: 16.5, lineHeight: 1.8, color: 'var(--sm-muted)', textAlign: 'justify' }}>
            Behind every commercial relationship is an operation that delivers: consistent supply, efficient logistics, and reliable execution of every commitment. The Group’s platform is engineered to move branded merchandise at scale, on time, and at the right value.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0, border: '1px solid var(--sm-line)', background: 'var(--sm-ivory)' }} className="cap-grid">
          {capabilities.map((c, i) => {
            const Icon = icons[i];
            return (
              <div key={c.number} style={{ padding: '40px 28px', borderRight: i < capabilities.length - 1 ? '1px solid var(--sm-line)' : 'none', display: 'flex', flexDirection: 'column', gap: 20, transition: 'background 300ms ease', cursor: 'default' }}
                   onMouseEnter={(e) => e.currentTarget.style.background = 'var(--sm-cream)'}
                   onMouseLeave={(e) => e.currentTarget.style.background = 'var(--sm-ivory)'}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, letterSpacing: '0.28em', color: 'var(--sm-gold)' }}>{c.number}</span>
                  <Icon size={20} color="var(--sm-navy)" strokeWidth={1.2} />
                </div>
                <div className="serif" style={{ fontSize: 22, color: 'var(--sm-navy)' }}>{c.title}</div>
                <p style={{ fontSize: 13.5, lineHeight: 1.7, color: 'var(--sm-muted)', margin: 0 }}>{c.desc}</p>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 96, display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 80, alignItems: 'center' }} className="biz-image-grid">
          <div style={{ overflow: 'hidden', aspectRatio: '4/5', position: 'relative' }}>
            <img src={warehouseImage} alt="Distribution warehouse" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', bottom: 20, left: 20, background: 'var(--sm-ivory)', padding: '14px 18px', maxWidth: 220 }}>
              <div style={{ fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--sm-gold)', marginBottom: 4 }}>Processing Centers</div>
              <div className="serif" style={{ fontSize: 18, color: 'var(--sm-navy)', lineHeight: 1.3 }}>Compton, CA · Medley, FL</div>
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <span className="hairline" />
              <span className="overline">Warehouse & Logistics</span>
            </div>
            <h3 className="serif" style={{ fontSize: 'clamp(28px, 3.4vw, 42px)', lineHeight: 1.15, color: 'var(--sm-navy)', margin: 0 }}>
              Scalable infrastructure across the United States.
            </h3>
            <p style={{ marginTop: 24, fontSize: 16, lineHeight: 1.8, color: 'var(--sm-muted)', textAlign: 'justify' }}>
              Two strategically located processing centers, on the West Coast in Compton, California, and on the East Coast in Medley, Florida, support rework, re-ticketing, sorting, and bagging at scale. Our infrastructure gives brand and retail partners a reliable one-stop solution for high-volume distribution.
            </p>

            <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {[
                { title: 'West Coast Hub', loc: 'Compton, California' },
                { title: 'East Coast Hub', loc: 'Medley, Florida' },
              ].map((h) => (
                <div key={h.title} style={{ padding: '18px 20px', background: 'var(--sm-ivory)', border: '1px solid var(--sm-line)' }}>
                  <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--sm-gold)', marginBottom: 8 }}>{h.title}</div>
                  <div className="serif" style={{ fontSize: 17, color: 'var(--sm-navy)' }}>{h.loc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1100px) {
          .cap-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .cap-grid > div:nth-child(odd) { border-right: 1px solid var(--sm-line) !important; }
          .cap-grid > div:nth-child(even) { border-right: none !important; }
          .cap-grid > div { border-bottom: 1px solid var(--sm-line); }
        }
        @media (max-width: 900px) {
          .biz-head, .biz-image-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
          .cap-grid { grid-template-columns: 1fr !important; }
          .cap-grid > div { border-right: none !important; border-bottom: 1px solid var(--sm-line); }
        }
      `}</style>
    </section>
  );
}
